/**
 * Public Contact Us page — accessible to logged-out visitors and logged-in
 * users alike. Opens a new message thread in the admin inbox.
 *
 * For logged-in users we skip the name/email fields (we know them from
 * auth context) and surface a link to /account/settings?tab=messages so
 * they can see prior replies.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Send } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import PageTitle from '../components/PageTitle';
import Seo from '../components/Seo';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sentThreadId, setSentThreadId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      toast.error('Please add a subject and a message.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        subject: subject.trim(),
        body: body.trim(),
      };
      if (!user) {
        if (!name.trim() || !email.trim()) {
          toast.error('Please add your name and email so we can reply.');
          setSubmitting(false);
          return;
        }
        payload.guest_name = name.trim();
        payload.guest_email = email.trim();
      }
      const r = await axios.post(`${API}/contact/threads`, payload, { withCredentials: true });
      setSentThreadId(r.data?.thread_id || true);
      setSubject('');
      setBody('');
      toast.success('Message sent — we will be in touch.');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Could not send your message.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (sentThreadId) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-lg w-full bg-white border border-[#E2E8F0] rounded-2xl p-8 text-center" data-testid="contact-thank-you">
          <div className="w-12 h-12 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-6 h-6 text-[#10B981]" />
          </div>
          <h1 className="text-2xl font-extrabold text-[#020617] mb-2">Thank you</h1>
          <p className="text-sm text-[#475569] leading-relaxed">
            Your message is in our inbox. We typically reply within 1–2 working days.
          </p>
          {user ? (
            <p className="text-sm text-[#475569] mt-3">
              You can see our reply in <Link to="/account/settings?tab=messages" className="text-[#0052FF] font-medium hover:underline">Account Settings → Messages</Link>.
            </p>
          ) : (
            <p className="text-sm text-[#475569] mt-3">
              We&apos;ll reply to <strong>{email}</strong>. Keep an eye on your inbox.
            </p>
          )}
          <Button
            asChild
            className="mt-6 bg-[#0052FF] text-white hover:bg-[#003ECC]"
            data-testid="contact-back-home-button"
          >
            <Link to={user ? '/dashboard' : '/'}>Back to {user ? 'dashboard' : 'home'}</Link>
          </Button>
        </div>
        </div>
        <Footer variant="full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="contact-page">
      <Seo
        title="Contact Forge Ledger — Get in touch"
        description="Reach the Forge Ledger team. No bots, no ticket queue — every message is read by a person and answered personally, usually within one working day."
        path="/contact"
      />
      <div className="flex-1 py-12 px-6">
      <div className="max-w-2xl mx-auto">
        <Link to={user ? '/dashboard' : '/'} className="inline-flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#020617] transition-colors mb-6" data-testid="contact-back-link">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>

        <PageTitle
          title="Contact us"
          subtitle="Send us a message and we will get back to you. No bots, no ticket queue — every message is read by a person."
          className="mb-8"
        />

        <form onSubmit={handleSubmit} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-5" data-testid="contact-form">
          {!user && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact-name">Your name</Label>
                <Input
                  id="contact-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Smith"
                  required
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Label htmlFor="contact-email">Email</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="contact-email-input"
                />
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="contact-subject">Subject</Label>
            <Input
              id="contact-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Billing question, feature idea, bug report"
              required
              maxLength={200}
              data-testid="contact-subject-input"
            />
          </div>

          <div>
            <Label htmlFor="contact-body">Message</Label>
            <Textarea
              id="contact-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Tell us what is on your mind. The more detail you give the faster we can help."
              required
              rows={7}
              maxLength={5000}
              data-testid="contact-body-input"
            />
            <p className="text-xs text-[#94A3B8] mt-1">{body.length} / 5000</p>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="bg-[#0052FF] text-white hover:bg-[#003ECC]"
            data-testid="contact-submit-button"
          >
            <Send className="w-4 h-4 mr-1.5" />
            {submitting ? 'Sending…' : 'Send message'}
          </Button>
        </form>
      </div>
      </div>
      <Footer variant="full" />
    </div>
  );
};

export default Contact;
 Contact;
